import { useCallback, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../api/auth/useAuth";
import { useNotificationWebSocket } from "../../../hooks/useNotificationWebSocket";
import { notificationKeys } from "../../../api/services/notification.hooks";
import type {
  Notification,
  UserNotification,
  GetUserNotificationsResponse,
} from "../../../api/services/notification.type";

/**
 * Connects WebSocket for notifications at Layout level.
 * Ensures connection starts as soon as user is logged in,
 * before any popover/menu is opened.
 */
export const NotificationWebSocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryClientRef = useRef(queryClient);

  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  const handleNewNotification = useCallback((notification: Notification) => {
    const client = queryClientRef.current;

    const prevUnread = client.getQueryData<{
      data: { unread_count: number };
    }>(notificationKeys.unreadCount());
    const newCount = (prevUnread?.data?.unread_count ?? 0) + 1;

    client.setQueryData(notificationKeys.unreadCount(), {
      data: { unread_count: newCount },
      error: "",
      message: "",
      success: true,
    });

    const listQuery = { page: 1, limit: 10 };
    const prevList = client.getQueryData<GetUserNotificationsResponse>(
      notificationKeys.userList(listQuery)
    );

    if (prevList?.data) {
      const newItem: UserNotification = {
        is_read: false,
        notification,
        read_at: null,
      };
      const exists = prevList.data.some(
        (item) => item.notification.id === notification.id
      );
      if (!exists) {
        client.setQueryData(notificationKeys.userList(listQuery), {
          ...prevList,
          data: [newItem, ...prevList.data],
        });
      }
    } else {
      void client.refetchQueries({ queryKey: notificationKeys.user() });
    }
  }, []);

  const handleUnreadCountUpdate = useCallback((count: number) => {
    queryClientRef.current.setQueryData(notificationKeys.unreadCount(), {
      data: { unread_count: count },
      error: "",
      message: "",
      success: true,
    });
    queryClientRef.current.invalidateQueries({
      queryKey: notificationKeys.user(),
    });
  }, []);

  useNotificationWebSocket({
    enabled: !!user,
    onNotification: handleNewNotification,
    onUnreadCountUpdate: handleUnreadCountUpdate,
  });

  return <>{children}</>;
};
